import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  ChatTurn,
  fetchEditThread,
  fetchLatestReport,
  listDealImages,
  requestAiEdit,
  saveReportVersion,
  uploadDealPhoto,
  uploadAnnotatedImage,
  uploadHeroImage,
  deleteReportVersions,
  downloadImage,
  emailReport,
  getDealContacts,
  getReportShareUrl,
  listReportComments,
  regenerateTaskListReport,
  respondToReportComment,
} from '../repositories/report.repository';
import { resolveReportBaseId } from '../lib/report/report-utils';
import type { PhotoRekey, TaskReassignment } from '../lib/report/report-ops';
import type { Report, ReportFile } from '../lib/report/types';

const reportKey = (dealId: string, baseId: string | null) => [
  'report',
  dealId,
  baseId,
];

/** The latest saved version of the report `file` belongs to. */
export function useReport(file: ReportFile | null, dealId: string) {
  const baseId = file
    ? file.reportID || resolveReportBaseId({ id: file.id })
    : null;
  return useQuery({
    queryKey: reportKey(dealId, baseId),
    queryFn: () => fetchLatestReport(file as ReportFile, dealId),
    enabled: !!file,
  });
}

export function useSaveReport() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      report,
      reassignments,
      rekeys,
    }: {
      report: Report;
      reassignments?: TaskReassignment[];
      rekeys?: PhotoRekey[];
    }) => saveReportVersion(report, reassignments, rekeys),
    onSuccess: (saved) => {
      queryClient.setQueryData(
        reportKey(saved.dealId, resolveReportBaseId(saved)),
        saved
      );
      queryClient.invalidateQueries({
        queryKey: ['deal-images', saved.dealId],
      });
    },
  });
}

export function useDealImages(dealId: string, enabled: boolean) {
  return useQuery({
    queryKey: ['deal-images', dealId],
    queryFn: () => listDealImages(dealId),
    enabled,
    staleTime: 60_000,
  });
}

/** Upload photos as deal photos (DealFiles) for use in a report. */
export function useUploadDealPhotos(dealId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (files: File[]) =>
      Promise.allSettled(files.map((file) => uploadDealPhoto(file, dealId))),
    onSettled: () =>
      queryClient.invalidateQueries({ queryKey: ['deal-images', dealId] }),
  });
}

export function useUploadCoverImage() {
  return useMutation({ mutationFn: uploadHeroImage });
}

/** A photo's bytes for the annotator; keyed by storage key, which never changes content. */
export function useImageBlob(key: string | null) {
  return useQuery({
    queryKey: ['image-blob', key],
    queryFn: () => downloadImage(key as string),
    enabled: !!key,
    staleTime: Infinity,
    gcTime: 5 * 60 * 1000,
  });
}

export function useUploadAnnotatedImage() {
  return useMutation({ mutationFn: uploadAnnotatedImage });
}

/**
 * Chat-driven editing (port of mobile useReportEditChat). The thread is server
 * state; the turns of an in-flight request live locally until the re-read
 * thread contains them. If the mutation errors (e.g. AppSync's timeout on a
 * long edit) the re-read still surfaces a reply the backend finished. Web
 * skips the realtime subscription — add it if cross-device sync is needed.
 */
export function useReportEditChat(params: {
  reportId: string | null;
  dealId: string;
  onNewVersion: () => void;
}) {
  const { reportId, dealId, onNewVersion } = params;
  const thread = useQuery({
    queryKey: ['report-edit-thread', reportId],
    queryFn: () => fetchEditThread(reportId as string),
    enabled: !!reportId,
  });
  const edit = useMutation({
    mutationFn: (message: string) =>
      requestAiEdit({ reportId: reportId as string, dealId, message }),
  });
  const [pending, setPending] = useState<ChatTurn[]>([]);

  const send = async (text: string) => {
    const message = text.trim();
    if (!message || !reportId || edit.isPending) {
      return;
    }
    const before = thread.data?.length ?? 0;
    const asked: ChatTurn = {
      turnId: `local-${Date.now()}`,
      role: 'user',
      text: message,
    };
    setPending([asked]);
    let reply: ChatTurn;
    try {
      const result = await edit.mutateAsync(message);
      reply = result.success
        ? {
            turnId: `assistant-${Date.now()}`,
            role: 'assistant',
            text: result.reply || 'Done.',
            status: 'done',
            resultingVersion: result.version,
          }
        : {
            turnId: `error-${Date.now()}`,
            role: 'assistant',
            text: `Sorry — I couldn't make that change${
              result.error ? `: ${result.error}` : '.'
            }`,
            status: 'error',
          };
    } catch (e) {
      console.error('[ReportChat] edit failed', e);
      reply = {
        turnId: `error-${Date.now()}`,
        role: 'assistant',
        text: 'Sorry — something went wrong making that change.',
        status: 'error',
      };
    }
    onNewVersion();
    const { data } = await thread.refetch();
    const grown = (data?.length ?? 0) - before;
    setPending(grown >= 2 ? [] : grown === 1 ? [reply] : [asked, reply]);
  };

  return {
    turns: [...(thread.data || []), ...pending],
    sending: edit.isPending,
    send,
  };
}

/** Customer phone/email from the deal (prefills share + comment replies). */
export function useDealContacts(deal: any, enabled: boolean) {
  return useQuery({
    queryKey: ['deal-contacts', deal?.id, deal?.categoryID],
    queryFn: () => getDealContacts(deal),
    enabled: enabled && !!deal,
    staleTime: 5 * 60_000,
  });
}

export function useShareReport() {
  return {
    getLink: useMutation({
      mutationFn: (v: { reportFileId: string; phone?: string }) =>
        getReportShareUrl(v.reportFileId, v.phone),
    }),
    email: useMutation({ mutationFn: emailReport }),
  };
}

export function useReportComments(reportId: string | null, enabled: boolean) {
  return useQuery({
    queryKey: ['report-comments', reportId],
    queryFn: () => listReportComments(reportId as string),
    enabled: enabled && !!reportId,
  });
}

export function useRespondToComment(reportId: string | null) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (v: { comment: string; phone: string }) =>
      respondToReportComment({ reportId: reportId as string, ...v }),
    onSettled: () =>
      queryClient.invalidateQueries({
        queryKey: ['report-comments', reportId],
      }),
  });
}

export function useDeleteReport() {
  return useMutation({
    mutationFn: (v: { reportId: string; dealId: string }) =>
      deleteReportVersions(v.reportId, v.dealId),
  });
}

export function useRegenerateReport() {
  return useMutation({ mutationFn: regenerateTaskListReport });
}
